/**
 * `/contacts/:id` — three columns on a desktop, three TABS on a phone.
 *
 * Left: identity + provenance + enrichment. Centre: the timeline. Right: the
 * action rail.
 *
 * Every panel is wrapped in its own `FeatureBoundary`. Design §2.12: "one dead
 * integration may never take down the CRM" — a timeline that throws must not
 * take the identity card with it, and an enrichment panel that 403s on a
 * suppressed contact must not blank the page.
 *
 * ## Mobile (owner report, 2026-08-29)
 *
 * The three columns were a `grid-cols-1` stack below `xl`, which is honest CSS
 * and a terrible phone screen: identity, then enrichment, then a scrolling
 * timeline, then — a page and a half down — the action rail with the sharing and
 * privacy controls. The timeline is unbounded, so on a real contact the rail was
 * unreachable in practice.
 *
 * Below `md` the same three groups become tabs. Tabs, not an accordion, because
 * the timeline needs the whole viewport height to be readable, and because
 * "Profile / Timeline / Actions" is the mental model the desktop layout already
 * teaches. The panels are the same components in both layouts; only the wrapper
 * differs, so there is no second copy of the page to keep in sync.
 *
 * The branch is `useIsMobile()` rather than a CSS `hidden`/`md:block` pair on
 * purpose: `display:none` leaves three tab panels' worth of focusable controls
 * in the tab order and in ⌘F, and the timeline would keep fetching pages nobody
 * can see.
 *
 * ## `?focus=<interactionId>`
 *
 * A digest citation links here with the interaction it cited. The param is
 * declared on the route (`parseContactDetailSearch`) so it is typed, bookmarkable
 * and validated; `InteractionTimeline` does the scrolling and the ring. On the
 * phone layout a focus param also selects the Timeline tab, so a citation from
 * an email or a push notification lands on the row rather than on the profile.
 *
 * ## What a SHARED contact renders instead
 *
 * A row reached through a grant carries `access.source === 'shared'`, a chip
 * naming the owner and the scope, an enrichment panel that explains the
 * per-workspace licensing rather than showing a blank, and — where the grant
 * allows it — an adopt dialog with a field-by-field diff. It has no share button
 * (a grant confers read, not the right to re-grant), no note composer, no
 * archive control and no list filing: every one of those is a write the owning
 * workspace owns.
 */

import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  cn,
  SkeletonRows,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  toast,
} from '@crm/ui';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { ArchiveIcon, PlusIcon } from 'lucide-react';
import { useMemo, useState } from 'react';
import {
  useContactQuery,
  useDeleteContactMutation,
  useOrgListQuery,
  useRestoreContactMutation,
  useSuppressionsQuery,
  useTimelineQuery,
} from '../../../api/queries.ts';
import { useOrgId } from '../../../api/session.ts';
import type { IncomingShareRow } from '../../../api/sharing.ts';
import { useIncomingSharesQuery } from '../../../api/sharing.ts';
import { ConfirmDialog } from '../../../components/confirm-dialog.tsx';
import { CommitmentsPanel } from '../../../components/contacts/commitments-panel.tsx';
import { ContactListsPanel } from '../../../components/contacts/contact-lists-panel.tsx';
import { ContactTypeBadge } from '../../../components/contacts/contact-type-badge.tsx';
import { EditContactDialog } from '../../../components/contacts/edit-contact-dialog.tsx';
import { EnrichmentPanel } from '../../../components/contacts/enrichment-panel.tsx';
import { deriveFieldSources } from '../../../components/contacts/field-sources.ts';
import { FollowUpPaceField } from '../../../components/contacts/follow-up-cadence.tsx';
import { metAtText } from '../../../components/contacts/met-via-field.tsx';
import { NextStepPanel } from '../../../components/contacts/next-step-panel.tsx';
import { XAccountLink } from '../../../components/contacts/x-account-link.tsx';
import { Planned } from '../../../components/placeholders.tsx';
import { SuppressionControls } from '../../../components/privacy/suppression-controls.tsx';
import { ProvenanceField } from '../../../components/provenance-field.tsx';
import {
  ErrorPanel,
  FeatureBoundary,
  RouteErrorPanel,
} from '../../../components/route-error-boundary.tsx';
import { AdoptDialog } from '../../../components/sharing/adopt-dialog.tsx';
import { ContactSharingPanel } from '../../../components/sharing/contact-sharing-panel.tsx';
import { ShareChip } from '../../../components/sharing/share-chip.tsx';
import { InteractionTimeline } from '../../../components/timeline/interaction-timeline.tsx';
import { TimelineNoteComposer } from '../../../components/timeline/note-composer.tsx';
import { ProfileNoteBlock } from '../../../components/timeline/profile-note-block.tsx';
import { useIsMobile } from '../../../hooks/useMediaQuery.ts';
import { toClientError } from '../../../lib/errors.ts';
import { formatDate, fullName } from '../../../lib/format.ts';
import { parseContactDetailSearch } from '../../../lib/search.ts';
import { detachedUndo } from '../../../lib/undo.ts';

export const Route = createFileRoute('/_authed/contacts/$contactId')({
  validateSearch: parseContactDetailSearch,
  component: ContactDetailPage,
  errorComponent: RouteErrorPanel,
});

function ContactDetailPage() {
  const { contactId } = Route.useParams();
  const { focus } = Route.useSearch();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const contact = useContactQuery(contactId);
  const timeline = useTimelineQuery(contactId);
  const [adopting, setAdopting] = useState(false);
  const [editing, setEditing] = useState(false);
  const [composingNote, setComposingNote] = useState(false);
  const [archiving, setArchiving] = useState(false);
  const archive = useDeleteContactMutation();
  const restore = useRestoreContactMutation();

  // A citation deep-link must land on the row, so it selects the Timeline tab.
  const [tab, setTab] = useState<string>(focus === undefined ? 'profile' : 'timeline');

  const items = useMemo(
    () => timeline.data?.pages.flatMap((page) => page.items) ?? [],
    [timeline.data],
  );

  const shared = contact.data?.access.source === 'shared';

  /**
   * The inbound grant this row arrived through, needed by the adopt dialog.
   *
   * `contacts.get` returns `access.shareId` but not the grant's other fields
   * (owner org name, `allowAdopt`, `adoptedContactId`), and there is no
   * `shares.get`. So the incoming inventory is read and matched on that id. It
   * is only fetched for a shared row — an owned contact never needs it.
   */
  const incoming = useIncomingSharesQuery({ resourceType: 'contact', enabled: shared });
  const shareId = contact.data?.access.shareId ?? null;
  const grant: IncomingShareRow | null =
    shareId === null ? null : (incoming.data?.items.find((row) => row.id === shareId) ?? null);
  /**
   * True once the inventory has answered and the grant was not in it — the first
   * page holds 50, and there is no per-share read to fall back to. The header
   * then says the grant could not be resolved rather than saying "Loading…"
   * forever.
   */
  const grantUnresolved = shared && grant === null && incoming.isSuccess;

  // Suppression flags for this contact. The list read is org-wide and cached, so
  // this costs nothing extra on a page that already sits behind the same query.
  const suppressions = useSuppressionsQuery(null);
  const suppression =
    suppressions.data?.items.find((entry) => entry.contactId === contactId) ?? null;

  // "Enrich for {ThisOrg}" needs the viewing workspace's NAME, and the session
  // carries only its id. `org.list` is the read that has both, and it is already
  // warm for the tenant switcher.
  const activeOrgId = useOrgId();
  const orgs = useOrgListQuery();
  const viewingOrgName = orgs.data?.items.find((org) => org.id === activeOrgId)?.name ?? null;

  if (contact.isLoading) {
    return <SkeletonRows rows={8} className="p-4" />;
  }

  if (contact.isError || contact.data === undefined) {
    return (
      <ErrorPanel
        error={contact.error}
        route={`/contacts/${contactId}`}
        reset={() => void contact.refetch()}
      />
    );
  }

  const detail = contact.data;
  const sources = deriveFieldSources(detail);
  const name = fullName(detail.firstName, detail.lastName);
  const canWrite = detail.access.canEdit && !shared;

  const identity = (
    <FeatureBoundary feature="Identity card">
      <div className="flex flex-col gap-4">
        <Card>
          <CardHeader>
            <div className="flex flex-wrap items-center gap-2">
              <CardTitle className="min-w-0 break-words text-base">{name}</CardTitle>
              <ContactTypeBadge type={detail.contactType} className="shrink-0" />
              {detail.access.canEdit ? (
                <Button
                  variant="ghost"
                  size="sm"
                  className="ml-auto shrink-0"
                  onClick={() => setEditing(true)}
                >
                  Edit
                </Button>
              ) : null}
            </div>
            <ShareChip
              source={detail.access.source}
              ownerOrgName={detail.access.ownerOrgName}
              scope={detail.access.scope}
              className="w-fit"
            />
            {shared && detail.access.canAdopt ? (
              <Button
                size="sm"
                variant="outline"
                className="mt-1 h-auto min-h-8 w-fit max-w-full whitespace-normal py-1.5 text-left"
                disabled={grant === null}
                onClick={() => setAdopting(true)}
                {...(grantUnresolved
                  ? {
                      title:
                        'The grant is not on the first page of your inbound shares, and there is no per-share read to fall back on. Open Settings → Sharing to find it.',
                    }
                  : {})}
              >
                {grant !== null
                  ? 'Adopt into this workspace…'
                  : grantUnresolved
                    ? 'Adopt — grant not found'
                    : 'Loading the grant…'}
              </Button>
            ) : null}
          </CardHeader>
          <CardContent className="flex flex-col divide-y divide-border">
            <ProvenanceField
              label="Company"
              value={detail.companyName}
              source={sources.companyName.source}
              capturedAt={sources.companyName.capturedAt}
            />
            <ProvenanceField
              label="Title"
              value={detail.title}
              source={sources.title.source}
              capturedAt={sources.title.capturedAt}
            />
            <ProvenanceField
              label="Email"
              value={detail.primaryEmail}
              source={sources.primaryEmail.source}
              capturedAt={sources.primaryEmail.capturedAt}
              confidence={sources.primaryEmail.confidence}
            />
            <ProvenanceField
              label="Phone"
              value={detail.primaryPhoneE164}
              source={sources.primaryPhone.source}
              capturedAt={sources.primaryPhone.capturedAt}
            />
            <ProvenanceField
              label="LinkedIn"
              value={
                detail.linkedinUrl === null ? null : (
                  <a
                    href={detail.linkedinUrl}
                    target="_blank"
                    rel="noreferrer noopener"
                    className="underline underline-offset-2"
                  >
                    {detail.linkedinSlug ?? detail.linkedinUrl}
                  </a>
                )
              }
              source={sources.linkedinUrl.source}
            />
            <ProvenanceField
              label="X"
              value={detail.xHandle === null ? null : <XAccountLink handle={detail.xHandle} />}
              source={sources.xHandle.source}
            />
            {/* The event when there is one, otherwise the channel they came
                through — "On X" beats "Not set" on a person the calendar never
                saw. `metAtText` owns that ranking. */}
            <ProvenanceField
              label="Met at"
              value={metAtText(detail.metAtEventName, detail.metVia)}
              source={sources.metAt.source}
              capturedAt={detail.metAt}
            />
            {/* Read-only here: the edit dialog is the one writer, so there is
                no second control that could disagree with it. */}
            <FollowUpPaceField days={detail.followUpCadenceDays} />
          </CardContent>
        </Card>

        <FeatureBoundary feature="Enrichment">
          <EnrichmentPanel
            contactId={contactId}
            // `canAdopt` is the GRANT's answer, not "have we loaded it yet".
            // Conflating the two would make the panel say "the owner did not
            // allow adopting" for the half-second before the inventory lands
            // — a false statement about someone else's decision.
            access={{
              source: detail.access.source,
              ownerOrgName: detail.access.ownerOrgName,
              canAdopt: detail.access.canAdopt,
            }}
            viewingOrgName={viewingOrgName}
            enrichedAt={detail.enrichedAt}
            enrichmentStatus={detail.enrichmentStatus}
            onAdopt={shared && grant !== null ? () => setAdopting(true) : undefined}
          />
        </FeatureBoundary>
      </div>
    </FeatureBoundary>
  );

  const timelinePanel = (
    <FeatureBoundary feature="Timeline">
      <Card className="flex min-h-0 flex-col overflow-hidden p-0">
        <CardHeader className="flex flex-row items-center justify-between border-b border-border">
          <CardTitle>Timeline</CardTitle>
          {/* One surface (owner decision 2026-08-30): notes are written, edited
              and deleted IN the timeline. The separate Notes panel is gone. */}
          {canWrite && !composingNote ? (
            <Button size="sm" variant="outline" onClick={() => setComposingNote(true)}>
              <PlusIcon aria-hidden />
              Note
            </Button>
          ) : null}
        </CardHeader>
        <TimelineNoteComposer
          contactId={contactId}
          contactName={detail.firstName}
          open={composingNote}
          onClose={() => setComposingNote(false)}
        />
        <InteractionTimeline
          items={items}
          isLoading={timeline.isLoading}
          error={timeline.error}
          hasNextPage={timeline.hasNextPage}
          isFetchingNextPage={timeline.isFetchingNextPage}
          onLoadMore={() => void timeline.fetchNextPage()}
          focusId={focus ?? null}
          editableNotes={canWrite}
        />
        <div className="border-t border-border p-3">
          <ProfileNoteBlock contact={detail} />
        </div>
      </Card>
    </FeatureBoundary>
  );

  const rail = (
    <FeatureBoundary feature="Action rail">
      <div className="flex flex-col gap-4">
        <FeatureBoundary feature="Next step">
          <NextStepPanel contactId={contactId} enabled={!shared} />
        </FeatureBoundary>

        <FeatureBoundary feature="Promises and asks">
          <CommitmentsPanel
            contactId={contactId}
            contactName={detail.firstName}
            enabled={!shared}
          />
        </FeatureBoundary>

        <FeatureBoundary feature="Lists">
          <ContactListsPanel contactId={contactId} contactName={name} canEdit={canWrite} />
        </FeatureBoundary>

        <FeatureBoundary feature="Sharing">
          <ContactSharingPanel contactId={contactId} contactName={name} canShare={!shared} />
        </FeatureBoundary>

        <FeatureBoundary feature="Privacy">
          <Card>
            <CardHeader>
              <CardTitle>Privacy</CardTitle>
            </CardHeader>
            <CardContent className="text-sm">
              {shared ? (
                <p className="text-muted-foreground">
                  Suppressions belong to the workspace that owns the record. Set them on your own
                  copy after adopting.
                </p>
              ) : (
                <>
                  <SuppressionControls contactId={contactId} suppression={suppression} />
                  <p className="mt-2 text-xs text-muted-foreground">
                    Honoured everywhere without an override: the recorder refuses to arm, the
                    enrichment planner refuses before it estimates, and Hermes never drafts.
                  </p>
                </>
              )}
            </CardContent>
          </Card>
        </FeatureBoundary>

        <Planned
          title="Related people"
          description="A short list of the people in your network closest to this one — same company, same event, or a similar story. Coming once the similarity index is in place."
        />

        <Card>
          <CardHeader>
            <CardTitle>Record</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col gap-1 text-xs text-muted-foreground">
            <span>Created {formatDate(detail.createdAt) ?? 'not shared'}</span>
            <span>Updated {formatDate(detail.updatedAt) ?? 'not shared'}</span>
            <span>
              Interactions{' '}
              {detail.interactionCount === null ? 'not shared' : detail.interactionCount}
            </span>
            {detail.adoptedFromShareId === null ? null : (
              <Badge variant="muted" className="mt-1 w-fit">
                adopted from a grant
              </Badge>
            )}
            <div className="mt-2 flex flex-wrap gap-2">
              <Button size="sm" variant="outline" onClick={() => void timeline.refetch()}>
                Refresh timeline
              </Button>
              {canWrite ? (
                <Button
                  size="sm"
                  variant="outline"
                  className="text-destructive"
                  onClick={() => setArchiving(true)}
                >
                  <ArchiveIcon aria-hidden />
                  Archive
                </Button>
              ) : null}
            </div>
          </CardContent>
        </Card>
      </div>
    </FeatureBoundary>
  );

  const dialogs = (
    <>
      <EditContactDialog contact={detail} open={editing} onOpenChange={setEditing} />

      {adopting && grant !== null ? (
        <AdoptDialog open onOpenChange={setAdopting} share={grant} />
      ) : null}

      <ConfirmDialog
        open={archiving}
        onOpenChange={setArchiving}
        title={`Archive ${name}?`}
        description="They come off your contact list and out of every digest. Nothing is deleted — the timeline, the notes and the promises stay, and the undo below brings the whole record back."
        confirmLabel="Archive"
        onConfirm={async () => {
          await archive.mutateAsync(contactId);
          toast.success(`${name} archived`, {
            // The undo is the ONLY route back: `contacts.list.includeDeleted` is
            // still a 422, so nothing in the app can list an archived contact.
            // A toast that vanishes is therefore the last chance — which is why
            // the action is on the toast rather than buried in a settings screen.
            duration: 10_000,
            // `detachedUndo`, NOT `restore.mutate(id, { onSuccess })`: this page
            // unmounts on the navigate below, and a mutation's per-call callbacks
            // do not fire once its observer has no listeners. See `lib/undo.ts`.
            action: detachedUndo({
              run: () => restore.mutateAsync(contactId),
              onDone: () => {
                toast.success(`${name} is back`);
                void navigate({
                  to: '/contacts/$contactId',
                  params: { contactId },
                  search: {},
                });
              },
              onFailed: (error) =>
                toast.error(`Could not bring ${name} back`, {
                  description: toClientError(error).message,
                }),
            }),
          });
          void navigate({ to: '/contacts', search: { source: 'all' } });
        }}
      />
    </>
  );

  if (isMobile) {
    return (
      <div className="flex min-h-0 flex-1 flex-col">
        <Tabs value={tab} onValueChange={setTab} className="flex min-h-0 flex-1 flex-col">
          <div className="border-b border-border px-3 py-2">
            <p className="mb-2 truncate text-base font-semibold">{name}</p>
            <TabsList className="w-full">
              <TabsTrigger value="profile" className="flex-1">
                Profile
              </TabsTrigger>
              <TabsTrigger value="timeline" className="flex-1">
                Timeline
              </TabsTrigger>
              <TabsTrigger value="actions" className="flex-1">
                Actions
              </TabsTrigger>
            </TabsList>
          </div>

          {/* `mt-0` overrides `TabsContent`'s desktop spacing; the panels bring
              their own padding and the tab strip is already a divider. */}
          <TabsContent value="profile" className="mt-0 min-h-0 flex-1 overflow-y-auto p-3">
            {identity}
          </TabsContent>
          <TabsContent value="timeline" className="mt-0 flex min-h-0 flex-1 flex-col p-3">
            {timelinePanel}
          </TabsContent>
          <TabsContent value="actions" className="mt-0 min-h-0 flex-1 overflow-y-auto p-3">
            {rail}
          </TabsContent>
        </Tabs>
        {dialogs}
      </div>
    );
  }

  return (
    <div
      className={cn(
        'grid min-h-0 flex-1 grid-cols-1 gap-4 p-4',
        'xl:grid-cols-[20rem_minmax(0,1fr)_18rem]',
      )}
    >
      {identity}
      {timelinePanel}
      {rail}
      {dialogs}
    </div>
  );
}
